/**
 * What the "Apply rotation" confirmation says it is about to do.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Counted per VERB, not per element, because the verbs are not
 * interchangeable: an elimination tombstones the element, a requeue puts it
 * back in the promotion line, and a park leaves it in Waiting until someone
 * moves it by hand. "3 changes" would hide exactly the distinction the user
 * needs before pushing to a tracker.
 */

import { matchTracker, type TrackerId, type RotationElementDiff } from '@trafficpulse/shared';

/** Human parts, in the order the rotation performs them. */
export function applySummaryParts(plan: RotationElementDiff[]): string[] {
  // A parked requeue is counted apart from a line requeue — same action on the
  // wire, different destination, and only one of them can come back on its own.
  const count = (action: string) =>
    plan.filter((d) => d.action === action && !d.toWaiting).length;
  const parked = plan.filter(
    (d) => d.action === 'requeue' && d.toWaiting,
  ).length;
  return [
    count('eliminate') ? `${count('eliminate')} elimination(s)` : null,
    count('requeue') ? `${count('requeue')} requeue(s)` : null,
    parked ? `${parked} to waiting` : null,
    count('promote') ? `${count('promote')} promotion(s)` : null,
    count('cap-pause') ? `${count('cap-pause')} offer cap pause(s)` : null,
    count('cap-reactivate')
      ? `${count('cap-reactivate')} cap reactivation(s)`
      : null,
  ].filter((part): part is string => part !== null);
}

/** The full confirm-dialog body: what will happen, then where it lands. */
export function applyDescription(
  plan: RotationElementDiff[],
  trackerWritesEnabled: boolean | undefined,
  provider: TrackerId = 'voluum',
  heldReason?: string | null,
): string {
  const parts = applySummaryParts(plan);
  const note = trackerWritesEnabled
    ? 'Changes push to the tracker.'
    : "With 'Add to tracker' OFF this only updates the local queue; nothing is pushed to the tracker.";
  const lead = parts.length
    ? `Apply ${parts.join(', ')}? `
    : 'No changes are planned right now — applying is a no-op. ';
  return lead + matchTracker(provider, { voluum: () => note, binom: () => note,
    'tres-commas': () => heldReason ? `Nothing pushed: ${heldReason}` : trackerWritesEnabled === false
      ? "Tres Commas checks write permissions before applying. With 'Add to tracker' OFF, only the local queue changes."
      : 'Tres Commas checks write permissions before pushing; held requests push nothing.' });
}
